import { useState } from "react";
import { ChevronDown, HelpCircle, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const FAQS = [
  {
    section: "Orders & Delivery",
    items: [
      {
        q: "How long does delivery take?",
        a: "Most orders are dispatched by the vendor within 24–48 hours of confirmation. Metro cities typically receive orders in 3–5 business days, and remote areas within 5–8 business days. You'll get a live AWB tracking number the moment your order is dispatched.",
      },
      {
        q: "Is shipping free?",
        a: "Yes — shipping is free on all orders above ₹1,500. Orders below ₹1,500 have a flat shipping fee of ₹49 that is transparently shown at checkout.",
      },
      {
        q: "Can I cancel my order?",
        a: "Orders can be cancelled at no cost as long as the vendor hasn't dispatched them. Once an AWB is generated, cancellation requires a return request through the standard return policy.",
      },
    ],
  },
  {
    section: "Products & Authenticity",
    items: [
      {
        q: "Are the products on Healthion India authentic?",
        a: "Every vendor on Healthion India is manually verified by Amiroop Healthion LLP — we check GST, FSSAI, AYUSH manufacturing/marketing licenses, PAN, and product Certificate of Analysis (COA) before they can list a single product. We do not manufacture — we curate.",
      },
      {
        q: "What does the Dosha tag on a product mean?",
        a: "Ayurveda classifies constitutions into three doshas — Vata (air), Pitta (fire) and Kapha (earth). Products are tagged with the dosha they best support. 'Tridoshic' products suit all constitutions.",
      },
      {
        q: "Do you sell prescription medicines?",
        a: "No. Healthion India lists only Ayurvedic, herbal, organic and holistic wellness products that comply with AYUSH and FSSAI regulations. For prescription medicines, please consult a licensed physician.",
      },
    ],
  },
  {
    section: "Vendor Onboarding",
    items: [
      {
        q: "How do I sell my brand on Healthion India?",
        a: "Create a vendor account, accept the Marketplace Vendor Agreement, then upload your GST, FSSAI, AYUSH license and PAN documents through our KYC form. Amiroop Healthion LLP typically reviews applications within 48 hours.",
      },
      {
        q: "What commission does Amiroop Healthion charge?",
        a: "A flat 12% marketplace commission on the net selling price (exclusive of GST). Standard payment gateway and shipping actuals are deducted transparently. Full terms are in the Marketplace Vendor Agreement.",
      },
      {
        q: "When are vendor payouts settled?",
        a: "Payouts are processed every T+7 to T+10 business days after successful order delivery and expiry of the customer return window.",
      },
    ],
  },
  {
    section: "Payments & Loyalty",
    items: [
      {
        q: "What payment methods are accepted?",
        a: "UPI, credit/debit cards, net banking and Cash on Delivery (in select pincodes). All online payments are processed through PCI-DSS compliant Indian payment gateways.",
      },
      {
        q: "How do Healthion loyalty points work?",
        a: "Every ₹10 spent on Healthion India earns you 1 loyalty point. Points are visible on your account dashboard and will soon be redeemable against future purchases and exclusive vendor drops.",
      },
      {
        q: "Do you have promo codes?",
        a: "Yes — new customers can use WELCOME20 for 20% off their first order. HEALTHION10 gives 10% off site-wide. Codes are applied at checkout.",
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="faq-page">
      <div className="mb-12 text-center">
        <div className="inline-flex badge-sage items-center gap-1"><HelpCircle className="w-3 h-3" /> Support Centre</div>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">Frequently asked questions</h1>
        <p className="mt-3 text-[#5A6B5D] max-w-2xl mx-auto">
          Everything you need to know about ordering, our verified vendor network, and Ayurveda essentials.
          Can't find your answer? Message us on WhatsApp.
        </p>
      </div>

      <div className="space-y-10">
        {FAQS.map((sec) => (
          <section key={sec.section}>
            <h2 className="font-serif text-2xl font-bold text-[#1E3A2B]">{sec.section}</h2>
            <div className="mt-4 space-y-2">
              {sec.items.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} testid={`faq-${sec.section.split(" ")[0].toLowerCase()}-${i}`} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-16 bg-[#1E3A2B] text-[#FAF8F5] rounded-3xl p-8 sm:p-10 text-center">
        <MessageCircle className="w-8 h-8 text-[#D97706] mx-auto" />
        <h3 className="mt-3 font-serif text-2xl font-bold">Still need help?</h3>
        <p className="mt-2 opacity-80 text-sm">Chat with our care team on WhatsApp — usually a reply within a few minutes.</p>
        <Link to="/return-policy" className="mt-6 inline-flex btn-amber text-sm">Read the Return Policy →</Link>
      </div>
    </div>
  );
}

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bg-white border rounded-2xl transition-all ${open ? "border-[#1E3A2B]" : "border-[#E2DDD5]"}`} data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left p-5"
      >
        <span className="font-serif text-lg font-semibold text-[#1A201C] pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#1E3A2B] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-[#5A6B5D] leading-relaxed">{a}</div>
      )}
    </div>
  );
}
